/**
 * Pure presentation logic for the Live Servers working-today roster (feat-086).
 *
 * Everything here is FORMAT + COLOR + ORDER only — never classification. The
 * server owns `match_state` and `per_location_health[].status`
 * (`feedback_metrics_single_source`); this module turns those locked enums into
 * sort order, chip styling, banner copy, and location-local time strings. No
 * "is today" / day-bucketing math lives in TypeScript.
 *
 * Split out of the component so the at-risk sort, the health→banner-copy
 * mapping, and the time formatters are unit-testable without React.
 */
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type {
  LocationHealthStatus,
  MatchState,
  RosterRow,
} from '../../services/liveServersService';
import { formatDuration, rowSeverityRank } from './deviceHealth';

/**
 * Display timezone for the pilot. Both allowlisted pilot locations are
 * `America/Los_Angeles` (contract §2/§5 — the allowlist is hardcoded to two LA
 * locations). The roster response intentionally carries raw UTC `Z` timestamps
 * and no per-location tz (locked contract §1); we format into the pilot zone
 * here. If the allowlist ever grows to a location in a different zone, the
 * backend must add a per-location timezone field — do NOT infer it in TS.
 */
export const PILOT_DISPLAY_TIMEZONE = 'America/Los_Angeles';

/**
 * At-risk-first ordering of match states (most at-risk = smallest rank).
 *
 * feat-274 SUPERSEDED this as the sort key: `sortRosterRows` now ranks on the
 * cross-axis severity ladder in `deviceHealth.ts`, which interleaves device
 * health with match state so that a dead microphone outranks every mapping
 * problem. The match-state half of that ladder preserves this exact order, and
 * this map is retained as the canonical statement of it.
 */
export const MATCH_STATE_RANK: Record<MatchState, number> = {
  square_only_unmapped: 0,
  square_only: 1,
  bespoke_only: 2,
  both: 3,
};

/** The name a row renders under: Bespoke server name, else the Square display name. */
export function rowDisplayName(row: RosterRow): string {
  return row.server_name ?? row.vendor_display_name ?? '—';
}

/**
 * Sort rows most-urgent-first, then alphabetically by display name within a band.
 * Pure (returns a new array).
 *
 * feat-274: the rank is the cross-axis severity ladder (`rowSeverityRank`), not
 * `MATCH_STATE_RANK` alone — `stalled` and `no_telemetry` sort ABOVE
 * `square_only_unmapped`, because one is happening on the floor right now and the
 * other is back-office data hygiene. Rows carrying no device health rank on the
 * match axis alone, so the pre-feat-274 ordering is preserved exactly for them.
 * Faulted rows stay pinned under a single-location filter, as unmapped rows do.
 */
export function sortRosterRows(rows: RosterRow[]): RosterRow[] {
  return [...rows].sort((a, b) => {
    const rankDelta = rowSeverityRank(a) - rowSeverityRank(b);
    if (rankDelta !== 0) return rankDelta;
    return rowDisplayName(a).localeCompare(rowDisplayName(b));
  });
}

// ---------- Status chip styling ----------

export type ChipTone = 'success' | 'warning' | 'danger';

export interface MatchStateChip {
  label: string;
  tone: ChipTone;
  /** Only square_only_unmapped renders the "Unmapped Team Member" sub-badge. */
  unmapped: boolean;
}

/** Chip label + tone for a match state. Two reds, distinguished by label + sub-badge. */
export function matchStateChip(state: MatchState): MatchStateChip {
  switch (state) {
    case 'square_only_unmapped':
      return { label: 'Square only · Unmapped', tone: 'danger', unmapped: true };
    case 'square_only':
      return { label: 'Square only', tone: 'danger', unmapped: false };
    case 'bespoke_only':
      return { label: 'Bespoke only', tone: 'warning', unmapped: false };
    case 'both':
    default:
      return { label: 'Both', tone: 'success', unmapped: false };
  }
}

// ---------- Degraded banner copy ----------

export interface BannerCopy {
  headline: string;
  detail: string;
  /** Reassurance shown under every degraded banner — Bespoke rows still render (AC8). */
  note: string;
}

/**
 * Per-location degraded-banner copy for a health status. Returns `null` for `ok`
 * (no banner). `lastPollAgo`, when supplied, is appended to the `stale_poll`
 * headline (e.g. "(last updated 42 min ago)") — passed pre-formatted so this
 * mapping stays pure/time-free for tests.
 */
export function bannerCopyForStatus(
  status: LocationHealthStatus,
  locationName: string,
  lastPollAgo?: string | null,
): BannerCopy | null {
  const note = 'Bespoke shifts for this location still appear below.';
  switch (status) {
    case 'needs_reauth':
      return {
        headline: `Square needs to be re-authorized for ${locationName}.`,
        detail: 'Clock-in data below may be stale.',
        note,
      };
    case 'missing_scope':
      return {
        headline: `Square access for ${locationName} is missing the timecards permission.`,
        detail: "We can't see who's clocked in — re-connect from the location's Square panel.",
        note,
      };
    case 'stale_poll':
      return {
        headline: lastPollAgo
          ? `Square clock-in data for ${locationName} is stale (last updated ${lastPollAgo}).`
          : `Square clock-in data for ${locationName} is stale.`,
        detail: 'The roster below may be incomplete.',
        note,
      };
    case 'no_mapping':
      return {
        headline: `${locationName} isn't mapped to a Square location yet.`,
        detail: "Connect it from the location's Square panel to see clock-ins here.",
        note,
      };
    case 'ok':
    default:
      return null;
  }
}

// ---------- Device-health fault banners (feat-274) ----------

export type FaultBannerTone = 'danger' | 'attention' | 'neutral';

export interface FaultBanner {
  key: string;
  tone: FaultBannerTone;
  /** Bold lede. */
  lede: string;
  /** One actionable sentence. */
  detail: string;
}

/**
 * "Microphone health is unavailable" — the roster loaded but device health did
 * not. The two can fail independently and will, since one is a live read and the
 * other is a database read.
 *
 * The tempting shortcut is to render the rows and leave the Mic cell blank, and a
 * blank cell in a column of green chips reads as "fine". Instead every row renders
 * `unknown` AND this banner states the mechanism and names the fallback, so a
 * manager who has been told to watch this page instead of Sentry knows to go look
 * somewhere else rather than concluding from a quiet screen that nothing is wrong.
 */
export const HEALTH_UNAVAILABLE_BANNER: FaultBanner = {
  key: 'health-unavailable',
  tone: 'neutral',
  lede: 'Microphone health is unavailable.',
  detail:
    'The roster below is current, but nothing here can tell you whether these ' +
    'microphones are capturing audio. Check Sentry until this clears.',
};

/** "A", "A and B", "A, B and C", then "A, B and 2 more" beyond three. */
function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

function uniq(values: string[]): string[] {
  return values.filter((v, i) => v !== '' && values.indexOf(v) === i);
}

/**
 * Banners for the faults present in the CURRENTLY VISIBLE rows.
 *
 * One banner per severity, not one per row: three dead mics produce one red
 * banner reading "3 microphones are not capturing audio" with the names listed.
 * A healthy roster produces none, which is what makes a banner's presence
 * meaningful — and it is why this is derived from the rows themselves rather than
 * from a separate query that could disagree with the table under it.
 *
 * The red and amber banners are NEVER collapsed into one. They carry different
 * actions and different urgency, and both may render at once. `no_telemetry` is
 * amber and not red on purpose: it is absence of evidence — a phone in a walk-in
 * freezer looks identical to a dead one — whereas `stalled` is a positive
 * measurement, and absence of evidence must not shout as loudly.
 *
 * `nowMs` is the server frame. The quoted duration is the worst among the
 * affected rows; it is dropped from the sentence entirely if no affected row
 * carried the timestamp, rather than substituted from another field.
 */
export function deviceFaultBanners(rows: RosterRow[], nowMs: number): FaultBanner[] {
  const banners: FaultBanner[] = [];

  const stalled = rows.filter((r) => r.device_health?.state === 'stalled');
  if (stalled.length > 0) {
    const n = stalled.length;
    const names = joinNames(stalled.map(rowDisplayName));
    const locations = joinNames(uniq(stalled.map((r) => r.location_name)));
    const worst = worstDuration(stalled.map((r) => r.device_health?.since ?? null), nowMs);
    const where = locations ? `${names} at ${locations}` : names;
    banners.push({
      key: 'stalled',
      tone: 'danger',
      lede: n === 1 ? '1 microphone is not capturing audio.' : `${n} microphones are not capturing audio.`,
      detail:
        (worst ? `${where} — no audio for ${worst}. ` : `${where}. `) +
        'Check the phone and the mic cable, then restart the shift.',
    });
  }

  const silentPhones = rows.filter((r) => r.device_health?.state === 'no_telemetry');
  if (silentPhones.length > 0) {
    const n = silentPhones.length;
    const names = joinNames(silentPhones.map(rowDisplayName));
    const locations = joinNames(uniq(silentPhones.map((r) => r.location_name)));
    const worst = worstDuration(
      silentPhones.map((r) => r.device_health?.last_telemetry_at ?? null),
      nowMs,
    );
    const where = locations ? `${names} at ${locations}` : names;
    banners.push({
      key: 'no_telemetry',
      tone: 'attention',
      lede: n === 1 ? '1 phone has stopped reporting.' : `${n} phones have stopped reporting.`,
      detail:
        (worst ? `${where} — last seen ${worst} ago. ` : `${where}. `) +
        (n === 1
          ? 'It may be out of Wi-Fi range or out of battery.'
          : 'They may be out of Wi-Fi range or out of battery.'),
    });
  }

  return banners;
}

/** Longest elapsed time among the supplied timestamps, or null if none is usable. */
function worstDuration(isos: Array<string | null>, nowMs: number): string | null {
  let oldest: string | null = null;
  let oldestMs = Number.POSITIVE_INFINITY;
  isos.forEach((iso) => {
    if (!iso) return;
    const date = toUtcDate(iso);
    const t = date.getTime();
    if (Number.isNaN(t) || t >= oldestMs) return;
    oldestMs = t;
    oldest = iso;
  });
  return oldest === null ? null : formatDuration(oldest, nowMs);
}

// ---------- Time formatting (location-local) ----------

/** Treat a zone-less backend string as UTC (mirrors locationTime/liveServerStatus). */
function toUtcDate(dateString: string): Date {
  const hasZone = dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString);
  return parseISO(hasZone ? dateString : `${dateString}Z`);
}

/**
 * Location-local clock time, e.g. `9:02 AM`. Returns `fallback` (default `—`) for
 * empty/unparseable input. `timezone` defaults to the pilot zone.
 */
export function formatClockTime(
  iso: string | null | undefined,
  timezone: string = PILOT_DISPLAY_TIMEZONE,
  fallback = '—',
): string {
  if (!iso) return fallback;
  try {
    const date = toUtcDate(iso);
    if (Number.isNaN(date.getTime())) return fallback;
    return formatInTimeZone(date, timezone, 'h:mm a');
  } catch {
    return fallback;
  }
}

/**
 * Location-local calendar day for the header, e.g. `Tue, Jun 17`. Returns `''`
 * for empty/unparseable input.
 */
export function formatRosterDate(
  iso: string | null | undefined,
  timezone: string = PILOT_DISPLAY_TIMEZONE,
): string {
  if (!iso) return '';
  try {
    const date = toUtcDate(iso);
    if (Number.isNaN(date.getTime())) return '';
    return formatInTimeZone(date, timezone, 'EEE, MMM d');
  } catch {
    return '';
  }
}

/**
 * Relative "time ago" for the Last-activity column, e.g. `Just now`, `2 min ago`,
 * `3 hr ago`, `2d ago`. `referenceMs` should be the server `as_of` epoch so the
 * delta is computed in the server frame (skew-free — both timestamps are server
 * clock). Clamps a slightly-future anchor to "Just now".
 */
export function formatActivityAgo(
  iso: string | null | undefined,
  referenceMs: number,
): string {
  if (!iso) return '—';
  const date = toUtcDate(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const deltaSec = Math.floor((referenceMs - date.getTime()) / 1000);
  if (deltaSec < 60) return 'Just now';
  const mins = Math.floor(deltaSec / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Compact "updated Ns ago" for the header freshness line, e.g. `4s ago`,
 * `2m ago`, `1h ago`. `deltaMs` is client-measured (now − last fetch), so this
 * tracks how fresh the last poll is regardless of clock skew.
 */
export function formatUpdatedAgo(deltaMs: number): string {
  const secs = Math.max(0, Math.floor(deltaMs / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

/**
 * Absolute "June 23, 2026 at 11:24am" stamp for the footer's "Last updated"
 * line. `epochMs` is the client epoch of the last successful fetch — the same
 * anchor `formatUpdatedAgo` measures against — so this stamp advances in lockstep
 * with the "Updated Ns ago" counter resetting. Rendered in the pilot display
 * timezone to match the rest of the page; meridiem is lowercased ("am"/"pm") to
 * match the requested format. Returns '' for a non-finite/invalid epoch.
 */
export function formatLastUpdated(
  epochMs: number,
  timezone: string = PILOT_DISPLAY_TIMEZONE,
): string {
  if (!Number.isFinite(epochMs)) return '';
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const datePart = formatInTimeZone(date, timezone, 'MMMM d, yyyy');
    const time = formatInTimeZone(date, timezone, 'h:mm');
    const meridiem = formatInTimeZone(date, timezone, 'a').toLowerCase();
    return `${datePart} at ${time}${meridiem}`;
  } catch {
    return '';
  }
}
